import { ChangeEvent, useEffect, useMemo } from 'react';

import { handleGrouping } from 'utilitys/helpers/general';

import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { Dataset } from 'components/widgets/Trends/Trends';
import {
    getDataSetBySelectedItem,
    getPreDefinedCrossVersionsData,
    getPreDefinedListObj,
    mapEntries,
    mapOptions,
    preDefinedList,
} from 'components/widgets/Trends/Trends.utils';
import { SingleLineOptions } from 'components/widgets/Trends/components/TrendsGraph/TrendsGraph';

type OptionsWithNot = ISelectOption<string> & { note?: string };

export const useTrendsLogic = ({ ui, api, widgetId }) => {
    const { sendEventWithDimensions } = useEventWithDimensions();

    const groupedData = useMemo(() => {
        let crossVersionsData = {};
        if (api.crossVersionsTrends.data) {
            switch (ui.state.singleLine) {
                case 'Single Parameter (Actual value)':
                    crossVersionsData = api.crossVersionsTrends.data.actual?.graph;

                    break;
                case 'Multi Parameters (Normalized values)':
                    crossVersionsData = api.crossVersionsTrends.data.normalized?.graph;

                    break;
                case 'Pre-defined Combinations (Normalized values)':
                    crossVersionsData = getPreDefinedCrossVersionsData(
                        api.crossVersionsTrends.data.normalized?.graph,
                        preDefinedList,
                    );
                    break;
            }
        }
        return crossVersionsData ? handleGrouping(Object.values(crossVersionsData), 'title') : undefined;
    }, [api.crossVersionsTrends.data, ui.state.singleLine]);

    const { graphData, options } = useMemo(() => {
        let graphData = { dataset: [] as Dataset[] };
        let options = [] as unknown as ISelectOption<string> | OptionsWithNot[];

        if (api.crossVersionsTrends.data && ui.state.selectedOptions && groupedData) {
            const dataset = getDataSetBySelectedItem(
                ui.state.singleLine === 'Pre-defined Combinations (Normalized values)'
                    ? getPreDefinedListObj(ui.state.selectedOptions, preDefinedList)
                    : ui.state.selectedOptions,
                groupedData,
                ui.state.singleLine,
            );

            graphData = { dataset };

            switch (ui.state.singleLine) {
                case 'Single Parameter (Actual value)':
                    options = Object.entries(groupedData).map(mapEntries);
                    break;
                case 'Multi Parameters (Normalized values)':
                    options = Object.entries(groupedData).map(mapEntries);
                    break;
                case 'Pre-defined Combinations (Normalized values)':
                    options = preDefinedList.map(mapOptions);
                    break;
            }
        }

        return {
            graphData,
            options,
        };
    }, [api.crossVersionsTrends.data, groupedData, ui.state.singleLine, ui.state.selectedOptions]);

    const handleItemSelection = (value: ISelectOption<string> | ISelectOption<string>[]) => {
        const label = Array.isArray(value) ? value.map((item) => item.label).join(', ') : value.label;
        sendEventWithDimensions({
            category: 'Trends',
            action: 'drop-list selection',
            label,
        });
        ui.setters.setSelectedOptions(value);
    };

    const handleSingle = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        ui.setters.setSingleLine(value as SingleLineOptions);
        if (groupedData) {
            if (value === 'Pre-defined Combinations (Normalized values)') {
                const option = preDefinedList.filter((item) => item.selected).map(mapOptions)[0];
                ui.setters.setSelectedOptions(option);
            } else {
                const selectedByDefault = Object.entries(groupedData)
                    .filter((item: [string, any]) => {
                        return item[1][0].selectedByDefault;
                    })
                    .map(mapEntries);

                ui.setters.setSelectedOptions(selectedByDefault);
            }
        }
    };

    const handleSelectOptions = (value) => {
        handleItemSelection(value);
    };

    useEffect(() => {
        if (groupedData && !widgetId) {
            if (ui.state.singleLine === 'Pre-defined Combinations (Normalized values)') {
                const option = preDefinedList.filter((item) => item.selected).map(mapOptions)[0];
                ui.setters.setSelectedOptions(option);
            } else {
                const selectedByDefault = Object.entries(groupedData)
                    .filter((item: [string, any]) => {
                        return item[1][0].selectedByDefault;
                    })
                    .map(mapEntries);
                const defaultValue =
                    ui.state.singleLine === 'Single Parameter (Actual value)'
                        ? selectedByDefault[0]
                        : selectedByDefault;
                ui.setters.setSelectedOptions(defaultValue);
            }
        }
    }, [widgetId, groupedData, ui.state.singleLine]);
    return { logic: { graphData, options, handleSelectOptions, handleSingle } };
};
